import React, { useEffect, useRef, useState } from 'react';
import { createChart, IChartApi, CandlestickSeries, LineSeries, UTCTimestamp, createSeriesMarkers } from 'lightweight-charts';
import { TradeDetail } from '../../types';
import './TradingChart.css';

interface CandleData {
  time: number | UTCTimestamp;
  open: number;
  high: number;
  low: number;
  close: number;
  ema21?: number;
  ema50?: number;
}

interface TradingChartProps {
  trades: TradeDetail[];
  candles?: CandleData[];
  dateRange?: string;
}

const TradingChart: React.FC<TradingChartProps> = ({ trades, candles, dateRange }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!chartContainerRef.current) return;
    if (!candles || candles.length === 0) return;

    const chartHeight = isFullscreen ? window.innerHeight - 100 : 600;

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: chartHeight,
      layout: {
        background: { color: '#ffffff' },
        textColor: '#333',
      },
      grid: {
        vertLines: { color: '#e1e1e1' },
        horzLines: { color: '#e1e1e1' },
      },
    });

    chartRef.current = chart;

    // Add candlestick series (v5 API)
    const candlestickSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderVisible: false,
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350',
    });

    // Set candle data (cast to any to bypass strict typing)
    candlestickSeries.setData(candles as any);

    // Create markers for trades (both entry and exit)
    const markers: any[] = [];

    trades.forEach(trade => {
      const entryTime = trade.entryTime || trade.entry_time;
      const entryPrice = trade.entryPrice || trade.entry_price;
      const entryReason = trade.entryReason || trade.entry_reason || 'N/A';
      const exitTime = trade.exitTime || trade.exit_time;
      const exitPrice = trade.exitPrice || trade.exit_price;
      const exitReason = trade.exitReason || trade.exit_reason || 'N/A';
      const profitLoss = trade.profitLoss || trade.profit_loss || 0;

      // Add entry marker - Green arrow up for BUY, Red arrow down for SELL
      if (entryTime && entryPrice) {
        markers.push({
          time: (new Date(entryTime).getTime() / 1000) as UTCTimestamp,
          position: 'belowBar' as 'belowBar',
          color: trade.direction === 'BUY' ? '#26a69a' : '#ef5350',
          shape: (trade.direction === 'BUY' ? 'arrowUp' : 'arrowDown') as 'arrowUp' | 'arrowDown',
          text: trade.direction === 'BUY' ? 'BUY' : 'SELL',
        });
      }

      // Determine if exit was profit or stop loss
      const isProfit = profitLoss > 0;
      const exitColor = isProfit ? '#2196F3' : '#FFC107'; // Blue for profit, Yellow for stop loss

      // Add exit marker - Blue dot for profit, Yellow dot for stop loss
      if (exitTime && exitPrice) {
        markers.push({
          time: (new Date(exitTime).getTime() / 1000) as UTCTimestamp,
          position: 'aboveBar' as 'aboveBar',
          color: exitColor,
          shape: 'circle' as 'circle',
          text: 'EXIT',
        });
      }
    });

    // Set markers on the series using v5 API
    if (markers.length > 0) {
      createSeriesMarkers(candlestickSeries, markers);
    }

    // Fit content
    chart.timeScale().fitContent();

    return () => {
      chart.remove();
    };
  }, [trades, candles, isFullscreen]);

  return (
    <div className={isFullscreen ? 'fullscreen-wrapper' : ''}>
      <div className="chart-header">
        <h3>Price Chart with Trade Markers{dateRange}</h3>
        <button
          className="expand-button"
          onClick={() => setIsFullscreen(!isFullscreen)}
          title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
        >
          {isFullscreen ? '✕' : '⛶'}
        </button>
      </div>
      <div ref={chartContainerRef} className="chart-container" />
      {(!candles || candles.length === 0) && (
        <div className="no-data">
          No chart data available. Run a backtest to see price data and trades.
        </div>
      )}
    </div>
  );
};

export default TradingChart;
